import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Tooltip,
    Chip,
    Checkbox,
    Paper,
    Typography,
    Popover,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    InputBase,
    Divider,
    Popper,
    ClickAwayListener,
    Fade,
} from "@mui/material";

import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useAuth } from "../contexts/AuthContext";
import { useSpeechToText } from "../hooks/useSpeechToText";
import clinicalNoteService from "../services/clinicalNoteService";
import masterDataService, { TagDataDTO } from "../services/masterDataService";
import { formatToBackendDateTime } from "../utils/dateUtils";
import { parse } from "date-fns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";


const parseInitialDate = (data: any): Date => {
    if (!data) return new Date();
    const rawDate = data.dateTime || data.noteDate || data.createdAt || data.createdOn || data.date;
    if (!rawDate) return new Date();

    if (rawDate instanceof Date) return rawDate;

    if (Array.isArray(rawDate)) {
        const [y, m, d, h = 0, min = 0, s = 0] = rawDate;
        return new Date(y, m - 1, d, h, min, s);
    }

    if (typeof rawDate === "string") {
        let temp = parse(rawDate, "dd/MM/yyyy hh:mm a", new Date());
        if (!isNaN(temp.getTime())) return temp;

        temp = parse(rawDate, "dd/MM/yyyy HH:mm", new Date());
        if (!isNaN(temp.getTime())) return temp;

        temp = parse(rawDate, "dd/MM/yyyy", new Date());
        if (!isNaN(temp.getTime())) return temp;

        temp = new Date(rawDate);
        if (!isNaN(temp.getTime())) return temp;
    }

    const d = new Date(rawDate);
    if (!isNaN(d.getTime())) return d;

    return new Date();
};

interface ResidentProgressNoteFormProps {
    residentId: string | null;
    residentName?: string;
    onSuccess: () => void;
    onCancel?: () => void;
    isDialog?: boolean;
    initialData?: any;
    latestNoteContent?: string;
    skipFetch?: boolean;
}

const ResidentProgressNoteForm: React.FC<ResidentProgressNoteFormProps> = ({
    residentId,
    residentName,
    onSuccess,
    onCancel,
    isDialog = false,
    initialData,
    latestNoteContent,
    skipFetch,
}) => {
    const { user } = useAuth();
    const [date, setDate] = useState<Date | null>(() => parseInitialDate(initialData));
    const [note, setNote] = useState<string>(() => {
        if (initialData) return initialData.note || initialData.content || "";
        return "";
    });

    // Tags state
    const [availableTags, setAvailableTags] = useState<TagDataDTO[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
    const [tagsAnchorEl, setTagsAnchorEl] = useState<HTMLElement | null>(null);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagSearch, setTagSearch] = useState("");
    const tagsLoadedRef = useRef(false);
    const tagsOpen = Boolean(tagsAnchorEl);

    useEffect(() => {
        if (initialData) {
            setDate(parseInitialDate(initialData));
            setNote(initialData.note || initialData.content || "");
        } else {
            setDate(new Date());
            setNote("");
        }
        setSelectedTagIds(new Set());
    }, [initialData]);

    const [previousNoteText, setPreviousNoteText] = useState<string | null>(null);

    useEffect(() => {
        if (latestNoteContent !== undefined) {
            setPreviousNoteText(latestNoteContent);
            return;
        }

        if (skipFetch) return;

        const fetchLatestNote = async () => {
            if (!residentId || initialData) {
                setPreviousNoteText(null);
                return;
            }
            try {
                const notes = await clinicalNoteService.getProgressNotesByResident(residentId);
                if (notes && notes.length > 0) {
                    const sortedNotes = notes.sort((a: any, b: any) => {
                        const dateA = new Date(a.dateTime || a.noteDate || a.createdAt || 0).getTime();
                        const dateB = new Date(b.dateTime || b.noteDate || b.createdAt || 0).getTime();
                        return dateB - dateA;
                    });
                    const latest = sortedNotes[0];
                    if (latest) {
                        setPreviousNoteText(latest.note || latest.content || latest.subject || "");
                    }
                } else {
                    setPreviousNoteText(null);
                }
            } catch (error) {
                console.error("Failed to fetch latest note:", error);
            }
        };
        fetchLatestNote();
    }, [residentId, initialData, latestNoteContent]);

    // Fetch tags once
    const fetchTags = async () => {
        if (tagsLoadedRef.current) return;
        setTagsLoading(true);
        try {
            const tags = await masterDataService.getTagsByCategoryName("Note");
            setAvailableTags(tags.filter((t) => t.isActive !== false));
            tagsLoadedRef.current = true;
        } catch (err) {
            console.error("Failed to fetch progress note tags:", err);
        } finally {
            setTagsLoading(false);
        }
    };

    const handleOpenTagsDropdown = (event: React.MouseEvent<HTMLElement>) => {
        setTagsAnchorEl(event.currentTarget);
        fetchTags();
    };

    const handleCloseTagsDropdown = () => {
        setTagsAnchorEl(null);
        setTagSearch("");
    };

    const handleTagToggle = (tag: TagDataDTO) => {
        const newSelected = new Set(selectedTagIds);
        if (newSelected.has(tag.id)) {
            newSelected.delete(tag.id);
            // Remove the tag text from note
            setNote((prev) => {
                const escaped = tag.fieldValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                return prev
                    .replace(new RegExp(`,\\s*${escaped}`), "")
                    .replace(new RegExp(`${escaped},\\s*`), "")
                    .replace(new RegExp(`${escaped}`), "")
                    .trim();
            });
        } else {
            newSelected.add(tag.id);
            // Append tag text to note
            setNote((prev) => {
                const trimmed = prev.trim();
                if (!trimmed) return tag.fieldValue;
                if (trimmed.includes(tag.fieldValue)) return prev;
                return trimmed + ", " + tag.fieldValue;
            });
        }
        setSelectedTagIds(newSelected);
    };

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const handleSave = async () => {
        if (!residentId) {
            setError("No resident selected.");
            return;
        }
        if (!note.trim()) {
            setError("Please enter a note.");
            return;
        }

        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            if (initialData && initialData.id) {
                await clinicalNoteService.updateProgressNote(initialData.id, {
                    residentId: residentId,
                    dateTime: formatToBackendDateTime(date || new Date()) || "",
                    note: note.trim(),
                    updatedByName: user?.name || user?.username || "-",
                } as any);
                setSuccessMessage("Progress note updated successfully.");
            } else {
                await clinicalNoteService.createProgressNote({
                    residentId: residentId,
                    dateTime: formatToBackendDateTime(date || new Date()) || "",
                    note: note.trim(),
                    createdByName: user?.name || user?.username || "-",
                    updatedByName: user?.name || user?.username || "-",
                } as any);
                setSuccessMessage("Progress note saved successfully.");
                setPreviousNoteText(note.trim());
                setNote("");
                setDate(new Date());
                setSelectedTagIds(new Set());
            }

            if (onSuccess) onSuccess();
            setTimeout(() => setSuccessMessage(null), 1000);
        } catch (error: any) {
            console.error("Failed to save progress note:", error);
            setError(
                error?.response?.data?.message ||
                "Failed to save progress note. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    const initialNoteRef = useRef<string>("");
    const { isListening, startListening, stopListening, isSupported } = useSpeechToText({
        interimResults: true,
        continuous: true,
        onResult: (spokenText) => {
            const base = initialNoteRef.current.trim();
            const cleaned = spokenText.trim();
            setNote(base ? (cleaned ? `${base} ${cleaned}` : base) : cleaned);
        },
        onEnd: () => {
            initialNoteRef.current = "";
        },
    });

    const filteredTags = availableTags.filter((t) =>
        t.fieldValue.toLowerCase().includes(tagSearch.toLowerCase())
    );

    const content = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {successMessage && <Alert severity="success">{successMessage}</Alert>}

            {!initialData && previousNoteText && (
                <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => {
                        setNote((prev) => (prev ? prev + "\n" + previousNoteText : previousNoteText));
                    }}
                    sx={{ alignSelf: "flex-start", mb: 1 }}
                >
                    Copy Previous Note
                </Button>
            )}

            {user?.role === 'SUPER_ADMIN' && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        label="Date & Time"
                        value={date}
                        onChange={(newDate) => setDate(newDate)}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                </LocalizationProvider>
            )}

            {/* Note TextField */}
            <TextField
                label="Note"
                fullWidth
                multiline
                minRows={4}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter progress note..."
                InputProps={{
                    endAdornment: isSupported && (
                        <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                            <Tooltip title={isListening ? "Stop listening" : "Dictate (Speech to Text)"}>
                                <IconButton
                                    size="small"
                                    onClick={isListening ? stopListening : () => { initialNoteRef.current = note || ""; startListening(); }}
                                    color={isListening ? "error" : "default"}
                                    sx={{
                                        animation: isListening ? "pulse 1.2s infinite" : "none",
                                        "@keyframes pulse": {
                                            "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                            "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                            "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                                        },
                                    }}
                                >
                                    <MicIcon />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );

    const tagsDropdown = (
        <Box>
            <Button
                id="tags-dropdown-button"
                variant="outlined"
                size="small"
                startIcon={<LocalOfferIcon fontSize="small" />}
                endIcon={tagsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                onClick={handleOpenTagsDropdown}
                sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 500,
                    color: selectedTagIds.size > 0 ? "primary.main" : "text.secondary",
                    borderColor: selectedTagIds.size > 0 ? "primary.main" : "divider",
                    "&:hover": { borderColor: "primary.main", color: "primary.main" },
                }}
            >
                Add Progress Notes
                {selectedTagIds.size > 0 && (
                    <Chip
                        label={selectedTagIds.size}
                        size="small"
                        color="primary"
                        sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
                    />
                )}
            </Button>

            <Popper
                open={tagsOpen}
                anchorEl={tagsAnchorEl}
                placement="bottom-start"
                transition
                style={{ zIndex: 1300 }}
                modifiers={[
                    {
                        name: 'flip',
                        enabled: false,
                    },
                    {
                        name: 'preventOverflow',
                        enabled: false,
                    },
                ]}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={200}>
                        <Paper
                            elevation={4}
                            sx={{
                                mt: 0.5,
                                width: 280,
                                borderRadius: 2,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                                overflow: "hidden",
                            }}
                        >
                            <ClickAwayListener onClickAway={handleCloseTagsDropdown}>
                                <Box>
                                    {/* Search bar */}
                                    <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center" }}>
                                        <Box sx={{ minWidth: 36, display: "flex", alignItems: "center" }}>
                                            <SearchIcon fontSize="small" sx={{ color: "text.secondary", ml: 0.5 }} />
                                        </Box>
                                        <InputBase
                                            placeholder="Search tags…"
                                            value={tagSearch}
                                            onChange={(e) => setTagSearch(e.target.value)}
                                            autoFocus
                                            fullWidth
                                            sx={{ fontSize: "0.875rem" }}
                                        />
                                    </Box>
                                    <Divider />

                                    {/* Tag list */}
                                    {tagsLoading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                            <CircularProgress size={20} />
                                        </Box>
                                    ) : filteredTags.length === 0 ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2, textAlign: "center" }}>
                                            {tagSearch ? "No matching tags." : "No tags available."}
                                        </Typography>
                                    ) : (
                                        <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
                                            {filteredTags.map((tag) => {
                                                const isSelected = selectedTagIds.has(tag.id);
                                                return (
                                                    <ListItem
                                                        key={tag.id}
                                                        button
                                                        onClick={() => handleTagToggle(tag)}
                                                        selected={isSelected}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 2,
                                                            "&.Mui-selected": {
                                                                bgcolor: "primary.50",
                                                            },
                                                            "&:hover": { bgcolor: "action.hover" },
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 36, display: "flex", alignItems: "center" }}>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                size="small"
                                                                disableRipple
                                                                color="primary"
                                                                sx={{ p: 0.5 }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={tag.fieldValue}
                                                            primaryTypographyProps={{
                                                                variant: "body2",
                                                                fontWeight: isSelected ? 600 : 400,
                                                            }}
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    )}
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </Box>
    );

    if (isDialog) {
        return (
            <>
                <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {initialData ? "Edit" : "Add"} Add New Progress Note {residentName ? `- ${residentName}` : ""}
                </DialogTitle>
                <DialogContent dividers>{content}</DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
                    {tagsDropdown}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {onCancel && (
                            <Button onClick={onCancel} disabled={saving}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || !note.trim()}
                            sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
                        >
                            {saving ? (initialData ? "Updating..." : "Saving...") : initialData ? "Update" : "Save"}
                        </Button>
                    </Box>
                </DialogActions>
            </>
        );
    }

    return (
        <Box>
            {content}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {tagsDropdown}
                <Box sx={{ display: "flex", gap: 1 }}>
                    {onCancel && (
                        <Button variant="outlined" onClick={onCancel} disabled={saving}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !note.trim()}
                        sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
                    >
                        {saving ? (initialData ? "Updating..." : "Saving...") : initialData ? "Update" : "Save"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ResidentProgressNoteForm;